import React, { useMemo } from "react";

import { Button, EditableCard, TField } from "@amphore-dev/amphore-lib";
import { t } from "i18next";
import * as Yup from "yup";

import { useQueryClient } from "@tanstack/react-query";

import UserService from "@/services/UserService";
import WorkspaceService from "@/services/WorkspaceService";

import { useAuth } from "@/hooks/useAuth";

import { EditableAvatar } from "@/components/molecules";

import { meKeys, workspaceKeys } from "@/constants/CQueryKeys";

import { TUpdateWorkspacePayload, TWorkspaceSettings } from "@/types";

import { promiseToast } from "@/modules/index";

const NAME_MAX = 80;
const DESCRIPTION_MAX = 500;
const LOGO_MAX_SIZE = 5 * 1024 * 1024;

/**
 * A workspace's identity: its logo, name and description. Read by its
 * members, changed by its owners and admins - for anyone else the card
 * is read-only and the logo cannot be clicked.
 */
export const WorkspaceIdentityCard: React.FC<{
	workspace: TWorkspaceSettings;
}> = ({ workspace }) => {
	const queryClient = useQueryClient();
	const { updateUser } = useAuth();
	const { id, canManage } = workspace;

	const fields: TField[] = useMemo(
		() => [
			{
				name: "name",
				type: "input",
				label: t("workspace.identity.name"),
				placeholder: t("workspace.switcher.of", {
					name: workspace.ownerName,
				}),
			},
			{
				name: "description",
				type: "textarea",
				label: t("workspace.identity.description"),
			},
		],
		[workspace.ownerName]
	);

	const validationSchema = useMemo(
		() =>
			Yup.object().shape({
				name: Yup.string().max(
					NAME_MAX,
					t("forms.max", { value: NAME_MAX })
				),
				description: Yup.string().max(
					DESCRIPTION_MAX,
					t("forms.max", { value: DESCRIPTION_MAX })
				),
			}),
		[]
	);

	const saved = (next: TWorkspaceSettings) => {
		queryClient.setQueryData(workspaceKeys.detail(id), next);
		return next;
	};

	// The name is what the switcher shows: /me lists it.
	const refreshMe = () =>
		UserService.fetchMe().then((me) => {
			queryClient.setQueryData(meKeys.all, me);
			updateUser(me);
		});

	const handleSubmit = (values: object) => {
		const { name = "", description = "" } =
			values as TUpdateWorkspacePayload;
		const prom = WorkspaceService.update(id, { name, description })
			.then(saved)
			.then(refreshMe);
		promiseToast(prom);
		return prom;
	};

	const handleLogo = (file: File) => {
		const prom = WorkspaceService.updateLogo(id, file).then(saved);
		promiseToast(prom);
		return prom;
	};

	const removeLogo = () => {
		const prom = WorkspaceService.deleteLogo(id).then(saved);
		promiseToast(prom);
	};

	return (
		<div className="flex max-w-3xl flex-col gap-4 sm:flex-row sm:items-start">
			<div className="flex shrink-0 flex-col items-center gap-2">
				<EditableAvatar
					url={workspace.logo ?? undefined}
					rounded={false}
					className="h-28 w-28 rounded-xl"
					maxSize={LOGO_MAX_SIZE}
					disabled={!canManage}
					uploadLabel={t("workspace.identity.logo")}
					onChange={handleLogo}
				/>
				{canManage && !!workspace.logo && (
					<Button
						variant="ghost"
						color="neutral"
						size="sm"
						onClick={removeLogo}
					>
						{t("workspace.identity.removeLogo")}
					</Button>
				)}
			</div>
			<EditableCard
				title={t("workspace.identity.title")}
				className="w-full"
				fields={fields}
				values={{
					name: workspace.name ?? "",
					description: workspace.description ?? "",
				}}
				validationSchema={validationSchema}
				onSubmit={handleSubmit}
				readOnly={!canManage}
				editInModal
				formikWrapper
				editLabel={t("actions.edit")}
				submitLabel={t("actions.save")}
				cancelLabel={t("actions.cancel")}
			/>
		</div>
	);
};
